<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div class="report-wrapper">

      <!-- Sticky Filter Header -->
      <div class="table-header-filters">
        <v-row dense class="align-center flex-nowrap">
          <!-- Date From -->
          <v-col cols="auto" style="min-width: 140px; max-width: 160px;">
            <v-menu v-model="reportFromMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  outlined dense hide-details
                  :value="formatDateFrom"
                  label="Od datuma"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs" v-on="on"
                  class="filter-select">
                </v-text-field>
              </template>
              <v-date-picker v-model="dateFrom" no-title scrollable @input="reportFromMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Date To -->
          <v-col cols="auto" style="min-width: 140px; max-width: 160px;">
            <v-menu v-model="reportToMenu" offset-y :close-on-content-click="false">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  outlined dense hide-details
                  :value="formatDateTo"
                  label="Do datuma"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  v-bind="attrs" v-on="on"
                  class="filter-select">
                </v-text-field>
              </template>
              <v-date-picker v-model="dateTo" no-title scrollable @input="reportToMenu = false"></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Customer Search -->
          <v-col cols="auto" style="min-width: 180px; max-width: 260px;">
            <v-combobox
              label="Kupac"
              v-model="customerFilter"
              :items="customers"
              :search-input.sync="customerSearchInput"
              return-object
              item-text="name"
              hide-no-data
              clearable
              @change="onCustomerSelected"
              dense outlined hide-details
              class="filter-select">
              <template v-slot:item="{ item, on, attrs }">
                <v-list-item v-on="on" v-bind="attrs">{{ item.name }}</v-list-item>
              </template>
            </v-combobox>
          </v-col>

          <!-- Action Cluster -->
          <v-col cols="auto" class="ml-auto d-flex align-center" style="gap: 8px">
            <v-btn :disabled="!customerFilter || typeof customerFilter !== 'object'" class="search-btn" @click="getData()" small>
              <v-icon small class="mr-1">mdi-magnify</v-icon>
              Pretraži
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltip }">
                <v-menu offset-y left :close-on-content-click="true">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-btn icon class="export-btn" v-bind="attrs" v-on="{ ...tooltip, ...menu }">
                      <v-icon color="#00b3be">mdi-export-variant</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense style="border-radius: 12px; overflow: hidden; padding: 4px; min-width: 140px;">
                    <v-list-item @click="changeWidth()">
                      <v-icon size="18" color="#E53935" class="mr-2">mdi-file-pdf-box</v-icon>
                      <v-list-item-title>PDF</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="generateExcel()">
                      <v-icon size="18" color="#43A047" class="mr-2">mdi-file-excel</v-icon>
                      <v-list-item-title>Excel</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="generateCsv()">
                      <v-icon size="18" color="#0288D1" class="mr-2">mdi-file-delimited</v-icon>
                      <v-list-item-title>CSV</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
              <span>Izvoz podataka</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="to" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Report Content -->
      <div class="report-content-wrapper">
        <div class="d-flex justify-center">
          <div id="table_info" class="report-tables">
            <!-- Company Info (hidden, for PDF) -->
            <table id="firma" style="display: none; font-family: 'Roboto-Regular'">
              <tbody>
                <tr><td>{{ company ? $options.filters.capitalize(company.name) : '' }}</td></tr>
                <tr><td>{{ company ? company.address + ' ' + company.city : '' }}</td></tr>
                <tr><td>OIB: {{ company ? company.oib : '' }}</td></tr>
                <tr><td>Kupac: {{ customerFilter && customerFilter.name ? customerFilter.name : '' }}</td></tr>
              </tbody>
            </table>

            <!-- Title -->
            <div class="report-title-wrapper">
              <table justify="center" style="width: 100%" cellspacing="0" id="title">
                <thead>
                  <tr class="header">
                    <th colspan="5">
                      <p class="report-main-title">Kartica kupca{{ customerFilter && customerFilter.name ? ' — ' + customerFilter.name : '' }}</p>
                    </th>
                  </tr>
                </thead>
              </table>
            </div>

            <!-- Receipt Table -->
            <div class="glass-table-wrapper">
              <table justify="center" style="width: 100%; table-layout: fixed;" cellspacing="0" id="receiptTable">
                <colgroup>
                  <col style="width: 160px" />
                  <col style="width: 120px" />
                  <col style="width: 120px" />
                  <col style="width: 120px" />
                  <col style="width: 120px" />
                </colgroup>
                <thead>
                  <tr :class="['header', 'naslov']">
                    <th colspan="5">Računi</th>
                  </tr>
                  <tr :class="['header', 'naslov']">
                    <th style="text-align: left;">Broj računa</th>
                    <th style="text-align: right;">Iznos</th>
                    <th>Datum računa</th>
                    <th>Datum valute</th>
                    <th>Status računa</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="receipt in allReceipts" :key="receipt.id">
                    <td style="text-align: left;">
                      {{
                        receipt.designation.number.toString() +
                        receipt.company_details.rcpt_separator +
                        receipt.designation.location_id +
                        receipt.company_details.rcpt_separator +
                        receipt.designation.register_number.toString()
                      }}
                    </td>
                    <td style="text-align: right;">
                      {{
                        viewType === 'pdf'
                          ? $options.filters.money(receipt.total, 100, '').replace('kn', '')
                          : receipt.total / 100
                      }}
                    </td>
                    <td style="text-align: center;">
                      {{ $options.filters.localWithoutDot(receipt.issue_date_time, false) }}
                    </td>
                    <td style="text-align: center;">
                      {{ receipt.payment_due ? $options.filters.localWithoutDot(receipt.payment_due, false) : '-' }}
                    </td>
                    <td style="text-align: center;">
                      {{ statusLabel(receipt) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="row-total">
                    <td style="text-align: left; font-weight: 700;">Ukupno naplaćeno</td>
                    <td style="text-align: right;">
                      {{
                        viewType === 'pdf'
                          ? $options.filters.money(totals.paid, 100, '').replace('kn', '')
                          : totals.paid / 100
                      }}
                    </td>
                    <td colspan="3"></td>
                  </tr>
                  <tr class="row-total">
                    <td style="text-align: left; font-weight: 700;">Ukupno duguje</td>
                    <td style="text-align: right;">
                      {{
                        viewType === 'pdf'
                          ? $options.filters.money(totals.owed, 100, '').replace('kn', '')
                          : totals.owed / 100
                      }}
                    </td>
                    <td colspan="3"></td>
                  </tr>
                  <tr class="row-total row-grand-total">
                    <td style="text-align: left; font-weight: 700;">Ukupno</td>
                    <td style="text-align: right;">
                      {{
                        viewType === 'pdf'
                          ? $options.filters.money(totals.total, 100, '').replace('kn', '')
                          : totals.total / 100
                      }}
                    </td>
                    <td colspan="3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  </v-container>
</template>

<script>
import { DAY, clone } from '@/plugins/utils'
import { df, auth } from '@/plugins/firebase'
import state from '@/state'
import 'jspdf-autotable'
import { saveExcel } from '@/modules/all-reports/xlsx/config.js'
import EventBus from '@/plugins/event-bus'
import { initPdf, setTable, setPageNumber } from '@/modules/all-reports/pdf/config.js'
import { callAssociates } from '@/api/associates'

export default {
  name: 'CustomerCard',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],
  data () {
    return {
      dateFrom: new Date(new Date().getTime() - DAY).toISOString().substr(0, 10),
      dateTo: new Date().toISOString().substr(0, 10),
      reportFromMenu: undefined,
      reportToMenu: undefined,
      company: undefined,
      allReceipts: [],
      totals: { paid: 0, owed: 0, total: 0 },
      customerFilter: null,
      customers: [],
      customerSearchInput: null,
      tableWidth: '1200px',
      a4PortraitWidth: '575px',
      targetModifier: '',
      viewType: 'pdf',
      margin: '0px'
    }
  },
  computed: {
    to () {
      return { name: `${this.targetModifier}.all-reports.reportMenu` }
    },
    formatDateFrom () {
      return new Intl.DateTimeFormat('hr-HR', { month: '2-digit', day: '2-digit', year: 'numeric' }).format(new Date(this.dateFrom))
    },
    formatDateTo () {
      return new Intl.DateTimeFormat('hr-HR', { month: '2-digit', day: '2-digit', year: 'numeric' }).format(new Date(this.dateTo))
    }
  },
  async created () {
    this.margin = state.getMargin()
  },
  mounted () {
    const sideNav = this.$route.meta.sideNav
    switch (sideNav) {
      case 'Company': this.targetModifier = 'company'; break
      case 'PointOfSale':
      case 'CashRegister': this.targetModifier = 'pointOfSale'; break
    }
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    this.company = clone(state.getCurrentCompany())
  },
  watch: {
    customerSearchInput (val) {
      if (val && val.length >= 3) {
        this.searchCustomers(val)
      } else if (!val || val.length === 0) {
        this.customers = []
      }
    }
  },
  methods: {
    statusLabel (receipt) {
      const status = receipt.payment?.current_status
      const map = {
        PAID: 'Plaćeno',
        UNPAID: 'Neplaćeno',
        REVERSED: 'Storniran',
        UNCOLLECTIBLE: 'Nenaplativo',
        EMAIL_SENT: 'Opomena poslana',
        WARNING_SENT: 'Upozorenje poslano'
      }
      return map[status] || status || '-'
    },
    async getData () {
      if (!this.customerFilter || typeof this.customerFilter !== 'object') return
      this.showLoader()
      const dateFrom = new Date(this.dateFrom).getTime() / 1000
      const dateTo = new Date(this.dateTo).getTime() / 1000
      this.allReceipts = []

      const oib = this.customerFilter.oib || (this.customerFilter.oibs && this.customerFilter.oibs[0])

      let query = df.collection(`receipts/${state.getCurrentCompany().id}/receipts`)
        .orderBy('issue_date_time', 'desc')

      if (oib) {
        query = query.where('type.payer_oib', '==', oib)
      } else {
        query = query.where('type.payer_name', '==', this.customerFilter.name)
      }

      await query.get().then(snap => {
        if (snap.docs && !snap.docs.empty) {
          snap.docs.forEach(doc => {
            const r = doc.data()
            if (r.issue_date_time >= dateFrom && r.issue_date_time <= dateTo + 86399) {
              this.allReceipts.push(r)
            }
          })
        }
      })

      this.allReceipts.sort((a, b) => a.issue_date_time - b.issue_date_time)
      this.calculateTotals()
      this.hideLoader()
    },
    calculateTotals () {
      let paid = 0
      let owed = 0
      this.allReceipts.forEach(r => {
        const amount = r.total || 0
        if (r.payment?.current_status === 'PAID' || r.payment?.current_status === 'REVERSED') {
          paid += amount
        } else {
          owed += amount
        }
      })
      this.totals = { paid, owed, total: paid + owed }
    },
    onCustomerSelected (val) {
      if (val && typeof val === 'object') {
        this.getData()
      }
    },
    async searchCustomers (searchTerm) {
      try {
        const term = searchTerm.split(',')[0].trim()
        const path = `/associates/${state.getCurrentCompany().id}/associate?filter=trigram(eq:${encodeURIComponent(term)})`
        const response = await callAssociates('get', path)
        if (response && response.status === 200 && response.data && response.data.associates) {
          this.customers = Object.keys(response.data.associates)
            .map(key => response.data.associates[key])
            .filter(a => a.status === 'OK' && a.associate_type.BUYER)
        } else if (response.status === 404) {
          this.customers = []
        }
      } catch (error) {
        console.error('Error searching customers:', error)
      }
    },
    changeWidth () {
      this.showLoader('', { opacity: 1 })
      const tableInfo = document.getElementById('table_info')
      tableInfo.style.width = this.a4PortraitWidth
      tableInfo.style.maxWidth = this.a4PortraitWidth
      tableInfo.style.fontSize = '80%'
      this.generatePdf()
    },
    async generatePdf () {
      const that = this
      var doc = await initPdf()
      doc = await setTable(doc, '#firma', 10)
      let finalY = doc.lastAutoTable.finalY || 10
      doc = await setTable(doc, '#title', finalY)
      finalY = doc.lastAutoTable.finalY || 10
      doc = await setTable(doc, '#receiptTable', finalY)
      doc = await setPageNumber(doc)
      doc.save('Kartica kupca.pdf')
      const tableInfo = document.getElementById('table_info')
      tableInfo.style.width = '100%'
      tableInfo.style.maxWidth = that.tableWidth
      tableInfo.style.fontSize = '100%'
      this.viewType = 'pdf'
      that.hideLoader()
    },
    generateExcel () {
      this.showLoader('', { opacity: 1 })
      this.viewType = 'excel'
      setTimeout(() => {
        saveExcel(['firma', 'title', 'receiptTable'], 'Kartica kupca.xlsx')
        this.viewType = 'pdf'
        this.hideLoader()
      }, 1000)
    },
    generateCsv () {
      this.showLoader('', { opacity: 1 })
      this.viewType = 'excel'
      setTimeout(() => {
        const table = document.getElementById('receiptTable')
        if (!table) { this.viewType = 'pdf'; this.hideLoader(); return }
        const rows = Array.from(table.querySelectorAll('tr'))
        const csv = rows.map(row =>
          Array.from(row.querySelectorAll('th,td'))
            .map(cell => '"' + cell.innerText.replace(/"/g, '""') + '"')
            .join(',')
        ).join('\n')
        const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'Kartica kupca.csv'
        a.click()
        URL.revokeObjectURL(url)
        this.viewType = 'pdf'
        this.hideLoader()
      }, 500)
    }
  }
}
</script>

<style scoped>
.report-wrapper {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

.table-header-filters {
  position: -webkit-sticky;
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #ffffff;
  padding: 16px;
  margin-bottom: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
}

.table-header-filters >>> .v-row {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

.search-btn {
  height: 36px !important;
  padding: 0 14px !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  text-transform: none !important;
  letter-spacing: 0.3px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  transition: all 0.2s ease !important;
}

.search-btn:hover:not(:disabled) {
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
}

.search-btn:disabled {
  opacity: 0.45 !important;
}

.export-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(0, 179, 190, 0.06) !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  transition: all 0.2s ease !important;
}

.export-btn:hover {
  background: rgba(0, 179, 190, 0.12) !important;
  box-shadow: 0 0 0 3px rgba(0, 179, 190, 0.12) !important;
}

.report-content-wrapper {
  margin-top: 40px;
}

.report-tables {
  width: 100%;
  max-width: 1200px;
  font-size: 16px;
  font-family: 'Roboto-Regular';
  margin: 0 auto;
}

@media (max-width: 1200px) {
  .report-tables { max-width: 100%; overflow-x: auto; }
}

.report-title-wrapper {
  margin-bottom: 20px;
}

.report-main-title {
  font-weight: 700;
  font-size: 24px;
  color: #2d3748;
  -webkit-text-fill-color: #2d3748;
  letter-spacing: 0.3px;
  display: inline-block;
  margin: 0;
  padding: 10px 0;
}

.glass-table-wrapper {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 16px;
  overflow: hidden;
}

.glass-table-wrapper table { border-collapse: collapse; width: 100%; border: none; }
.glass-table-wrapper table th, .glass-table-wrapper table td { padding: 14px 20px; border: none; border-bottom: 1px solid #EDF2F7; font-variant-numeric: tabular-nums; }
.glass-table-wrapper table tbody tr:hover:not(.naslov):not(.row-total) { background: #F7FAFC; }

.naslov { background: #F8FAFC !important; font-weight: 700 !important; font-size: 13px !important; text-transform: uppercase !important; letter-spacing: 1px !important; color: #2D3748 !important; }
.naslov th, .naslov td { color: #2D3748 !important; font-weight: 700 !important; }
.row-total { background: rgba(0, 188, 212, 0.03) !important; font-weight: 700 !important; border-top: 2px solid rgba(0, 188, 212, 0.2) !important; color: #00838F !important; }
.row-total td, .row-total th { color: #00838F !important; font-weight: 700 !important; border-bottom: 1px solid rgba(0, 188, 212, 0.1) !important; }
.row-grand-total td { border-bottom: 2px solid rgba(0, 188, 212, 0.2) !important; }

.table-header-filters + .report-content-wrapper {
  margin-top: 0 !important;
}
</style>
